import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-action-primary text-white hover:bg-action-primary-hover shadow-md hover:shadow-glow focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        secondary:
          'bg-bg-muted text-text-secondary hover:bg-bg-subtle border border-border-default dark:bg-bg-subtle dark:text-text-primary dark:hover:bg-bg-subtle dark:border-border-strong focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        ghost:
          'text-text-secondary hover:text-text-primary hover:bg-bg-muted dark:text-text-muted dark:hover:text-text-primary dark:hover:bg-bg-elevated focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        danger:
          'bg-feedback-error text-white hover:bg-action-danger-hover focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        'ghost-danger':
          'text-text-subtle hover:text-feedback-error hover:bg-feedback-error/10 focus:ring-feedback-error focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        outline:
          'border border-border-accent text-action-primary hover:bg-bg-accent-subtle dark:text-text-accent dark:hover:bg-action-primary-hover/10 focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export { buttonVariants };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  // Default to type="button" so a Button placed inside a <form> never submits it
  // implicitly. Callers that want submit behavior must opt in with type="submit".
  ({ className, variant, size, type = 'button', ...props }, ref) => {
    return <button type={type} className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);

Button.displayName = 'Button';
